import { useCallback, useEffect, useState } from 'react';
import { bffQuery } from '../lib/bffClient';

/**
 * The clinic's services as a BOOKING surface reads them (PM-0468 §2.4,
 * `accounting.ListBookableServices` through the clinic BFF's `bookableServices`):
 * what each one is, how long it takes, who performs it and the two offering
 * flags. One answer for the desk's «ثبت نوبت», the person drawer's «برنامه»
 * (which services may a shift be narrowed to) and the readiness strip.
 *
 * `providerType` / `providerId` narrow the list to what THAT person performs —
 * a row naming them, a performer-less row, a doctor's own price row, or a role
 * they hold. `patientFacing` returns only what the public sees.
 *
 * `loaded` is distinct from `services.length`: an empty list from the server
 * means «this person performs nothing», while «not loaded yet» must not be
 * read as that (a tab that flickers away and back is worse than one that
 * arrives a beat late).
 */
export const BOOKABLE_SERVICE_FIELDS = `
  declarationId catalogId name category orderType schedulingMode
  durationMinutes durationSource unitPrice currency isActive doctorId
  performers { type id label isDefault members { type id name active } }
  patientBookable publicVisible prepInstructions
`;

export const BOOKABLE_SERVICES = `
  query BookableServices($providerType: PerformerType, $providerId: ID, $patientFacing: Boolean) {
    bookableServices(providerType: $providerType, providerId: $providerId, patientFacing: $patientFacing) {
      ${BOOKABLE_SERVICE_FIELDS}
    }
  }
`;

export function useBookableServices({
  providerType = null, providerId = null, patientFacing = false, enabled = true,
} = {}) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(!!enabled);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!enabled) { setServices([]); setLoaded(false); setLoading(false); setError(null); return; }
    setLoading(true);
    try {
      const d = await bffQuery(BOOKABLE_SERVICES, {
        providerType: providerType || null,
        providerId: providerId || null,
        patientFacing: !!patientFacing,
      }, 'BookableServices');
      setServices(d?.bookableServices || []);
      setLoaded(true);
      setError(null);
    } catch (e) {
      setServices([]);
      setLoaded(false);
      setError(e?.message || 'bookableServices');
    } finally {
      setLoading(false);
    }
  }, [enabled, providerType, providerId, patientFacing]);

  useEffect(() => { load(); }, [load]);

  return { services, loading, loaded, error, reload: load };
}

export default useBookableServices;
